import React, { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Activity, ArrowLeft, Check, CheckCircle2, ChevronLeft, ChevronRight, ClipboardCheck, ClipboardList,
  FileText, Heart, MapPin, Pencil, Plus, Save, Shield, StickyNote, Stethoscope, TestTube, Trash2, User,
} from "lucide-react";
import type { ClientRecord, AnalyteKey, HivDiagnosis, SpecimenRow, SpecimenRows, StdSection, TestFlags, FormErrors } from "../../types";
import {
  COUNTIES, HOSPITALS, HOSPITAL_FACILITIES, RELATIONSHIPS, SOURCES, BIRTH_SEXES, ADDRESS_TYPES,
  TREATMENTS, REACTIVITY_RESULTS, LINKAGE_STATUSES, MODES_OF_TRANSMISSION, emptyRecord,
} from "../../data/lookups";
import { Field, Select, TextInput, Textarea, Checkbox, SectionHeading, inputCls } from "../../components/FormControls";

/* ------------------------------------------------------------------ */
/*  CLIENT DETAILS — MULTI-STEP WIZARD                                 */
/*  Steps mirror the legacy right-hand jump nav exactly.               */
/* ------------------------------------------------------------------ */

export type StepKey =
  | "patientId" | "contact" | "emergency" | "medical" | "linkage"
  | "testing" | "patientTreatment" | "hiv" | "hivUpdated" | "stdHistory"
  | "stdTests" | "stdTestsUpdated" | "notes" | "summary";

export interface StepDef {
  key: StepKey;
  label: string;
  icon: LucideIcon;
}
export const STEPS: StepDef[] = [
  { key: "patientId", label: "Patient Identification", icon: User },
  { key: "contact", label: "Patient Contact Information", icon: MapPin },
  { key: "emergency", label: "Emergency Contact Information", icon: Heart },
  { key: "medical", label: "Medical Information", icon: Stethoscope },
  { key: "linkage", label: "Linkage to Care Status", icon: Activity },
  { key: "testing", label: "Testing Information", icon: ClipboardCheck },
  { key: "patientTreatment", label: "Patient Treatment Info", icon: TestTube },
  { key: "hiv", label: "HIV Diagnosis Details", icon: Shield },
  { key: "hivUpdated", label: "Updated HIV Diagnosis Details", icon: Shield },
  { key: "stdHistory", label: "STD Treatment History", icon: FileText },
  { key: "stdTests", label: "STD Test Information", icon: ClipboardList },
  { key: "stdTestsUpdated", label: "Updated STD Test Information", icon: ClipboardList },
  { key: "notes", label: "Additional Notes", icon: StickyNote },
  { key: "summary", label: "Summary", icon: CheckCircle2 },
];

export function validatePatientTreatment(f: ClientRecord): FormErrors {
  const e: FormErrors = {};
  if (!f.naatResult) e.naatResult = "NAAT is Required";
  if (!f.disclosed) e.disclosed = "Disclosed is Required";
  if (!f.hivAgAbResult) e.hivAgAbResult = "HIV-1/2 Ag/Ab is Required";
  if (!f.inCare) e.inCare = "In Care is Required";
  if (!f.hivIaResult) e.hivIaResult = "HIV - 1/2 IA (Supplemental) is Required";
  return e;
}

export function validatePatientId(f: ClientRecord): FormErrors {
  const e: FormErrors = {};
  if (!f.firstName) e.firstName = "Required";
  if (!f.lastName) e.lastName = "Required";
  if (!f.dob) e.dob = "Required";
  if (!f.hospital) e.hospital = "Required";
  if (!f.facility) e.facility = "Required";
  if (!f.dateReceived) e.dateReceived = "Required";
  if (!f.medicalRecordNumber) e.medicalRecordNumber = "Required";
  return e;
}
export function validateContact(f: ClientRecord): FormErrors {
  const e: FormErrors = {};
  if (!f.street1) e.street1 = "Required";
  if (!f.city) e.city = "Required";
  if (!f.zip) e.zip = "Required";
  return e;
}

export interface ClientWizardProps {
  initial: ClientRecord | null;
  onCancel: () => void;
  onSave: (rec: ClientRecord) => void;
}
export function ClientWizard({ initial, onCancel, onSave }: ClientWizardProps) {
  const [stepIdx, setStepIdx] = useState<number>(0);
  const [form, setForm] = useState<ClientRecord>(initial || emptyRecord);
  const [errors, setErrors] = useState<FormErrors>({});
  const [visited, setVisited] = useState<Set<number>>(new Set([0]));

  const stepKey = STEPS[stepIdx].key;
  function update(patch: Partial<ClientRecord>) {
    setForm((f) => ({ ...f, ...patch }));
  }
  function updateNested(section: "testFlags", patch: Partial<TestFlags>): void;
  function updateNested(section: "hiv" | "hivUpdated", patch: Partial<HivDiagnosis>): void;
  function updateNested(section: "testFlags" | "hiv" | "hivUpdated", patch: Record<string, unknown>) {
    setForm((f) => ({ ...f, [section]: { ...(f[section] as object), ...patch } }));
  }
  function updateSpecimen(section: StdSection, analyte: AnalyteKey, rows: SpecimenRow[]) {
    setForm((f) => ({ ...f, [section]: { ...f[section], [analyte]: rows } }));
  }

  function goTo(idx: number) {
    setVisited((v) => new Set(v).add(idx));
    setStepIdx(idx);
  }

  function stepErrors(key: StepKey, f: ClientRecord): FormErrors {
    if (key === "patientId") return validatePatientId(f);
    if (key === "contact") return validateContact(f);
    if (key === "patientTreatment") return validatePatientTreatment(f);
    return {};
  }

  function next() {
    const errs = stepErrors(stepKey, form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (stepIdx < STEPS.length - 1) goTo(stepIdx + 1);
  }
  function back() {
    if (stepIdx > 0) goTo(stepIdx - 1);
  }

  function handleSave() {
    const errs = { ...validatePatientId(form), ...validateContact(form), ...validatePatientTreatment(form) };
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const badIdx = STEPS.findIndex((s) => Object.keys(stepErrors(s.key, form)).length > 0);
      if (badIdx >= 0) goTo(badIdx);
      return;
    }
    onSave(form);
  }

  const progressPct = Math.round(((stepIdx + 1) / STEPS.length) * 100);

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6">
      <button onClick={onCancel} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="mb-6 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{form.firstName} {form.lastName}</h2>
          <p className="text-xs text-slate-400">{form.eharsId ? `eHARS ${form.eharsId}` : form.id} · Step {stepIdx + 1} of {STEPS.length}</p>
        </div>
        <div className="w-full sm:w-56">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full rounded-full bg-cyan-600 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      <div className="flex flex-row items-start gap-6 overflow-x-auto">
        <ol className="w-64 shrink-0 flex flex-col gap-1">
          {STEPS.map((s, i) => {
            const state = i === stepIdx ? "current" : visited.has(i) ? "done" : "todo";
            return (
              <li key={s.key}>
                <button
                  onClick={() => goTo(i)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${
                    state === "current" ? "bg-cyan-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
                    state === "current" ? "bg-white text-cyan-700" : state === "done" ? "bg-cyan-50 text-cyan-700" : "bg-slate-200 text-slate-500"
                  }`}>
                    {state === "done" ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <span>{s.label}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-5 md:p-6">
          {stepKey === "patientId" && <StepPatientId form={form} update={update} errors={errors} />}
          {stepKey === "contact" && <StepContact form={form} update={update} errors={errors} />}
          {stepKey === "emergency" && <StepEmergency form={form} update={update} />}
          {stepKey === "medical" && <StepMedical form={form} update={update} />}
          {stepKey === "linkage" && <StepLinkage form={form} update={update} />}
          {stepKey === "testing" && <StepTesting form={form} update={update} updateNested={updateNested} />}
          {stepKey === "patientTreatment" && <StepPatientTreatment form={form} update={update} errors={errors} />}
          {stepKey === "hiv" && <StepHiv title="HIV Diagnosis Details" data={form.hiv} onChange={(p) => updateNested("hiv", p)} />}
          {stepKey === "hivUpdated" && <StepHiv title="Updated HIV Diagnosis Details" data={form.hivUpdated} onChange={(p) => updateNested("hivUpdated", p)} />}
          {stepKey === "stdHistory" && <StepStdHistory form={form} update={update} />}
          {stepKey === "stdTests" && <StepStdTests title="STD Test Information" section="stdTests" data={form.stdTests} onChange={updateSpecimen} />}
          {stepKey === "stdTestsUpdated" && <StepStdTests title="Updated STD Test Information" section="stdTestsUpdated" data={form.stdTestsUpdated} onChange={updateSpecimen} />}
          {stepKey === "notes" && <StepNotes form={form} update={update} />}
          {stepKey === "summary" && <StepSummary form={form} goTo={goTo} />}

          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
            <button
              onClick={back}
              disabled={stepIdx === 0}
              className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium text-slate-500 transition hover:bg-slate-100 disabled:opacity-0"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <div className="flex items-center gap-2">
              <button onClick={onCancel} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              {stepIdx < STEPS.length - 1 ? (
                <button onClick={next} className="flex items-center gap-1.5 rounded-full bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700">
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={handleSave} className="flex items-center gap-1.5 rounded-full bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700">
                  <Save className="h-4 w-4" /> Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Steps ------------------------------ */

export interface StepFormProps {
  form: ClientRecord;
  update: (patch: Partial<ClientRecord>) => void;
  errors?: FormErrors;
}

export function StepPatientId({ form, update, errors = {} }: StepFormProps) {
  const facilities = HOSPITAL_FACILITIES[form.hospital] || [];
  return (
    <div>
      <SectionHeading title="Patient Identification" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Source">
          <Select value={form.source} onChange={(e) => update({ source: e.target.value })}>
            {SOURCES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="First Name" required error={errors.firstName}>
          <TextInput value={form.firstName} error={errors.firstName} onChange={(e) => update({ firstName: e.target.value })} />
        </Field>
        <Field label="Middle Name">
          <TextInput value={form.middleName} onChange={(e) => update({ middleName: e.target.value })} />
        </Field>
        <Field label="Last Name" required error={errors.lastName}>
          <TextInput value={form.lastName} error={errors.lastName} onChange={(e) => update({ lastName: e.target.value })} />
        </Field>
        <Field label="Date of Birth" required error={errors.dob}>
          <TextInput type="date" value={form.dob} error={errors.dob} onChange={(e) => update({ dob: e.target.value })} />
        </Field>
        <Field label="Birth Sex">
          <Select value={form.birthSex} onChange={(e) => update({ birthSex: e.target.value })}>
            <option value="">Select Birth Sex</option>
            {BIRTH_SEXES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Routinized Testing Focus Project – Hospital" required error={errors.hospital}>
          <Select value={form.hospital} error={errors.hospital} onChange={(e) => update({ hospital: e.target.value, facility: "" })}>
            <option value="">----- Select Routinized Hospitals -----</option>
            {HOSPITALS.map((h) => <option key={h}>{h}</option>)}
          </Select>
        </Field>
        <Field label="Routinized Testing Focus Project – Facility" required error={errors.facility}>
          <Select value={form.facility} error={errors.facility} onChange={(e) => update({ facility: e.target.value })} disabled={!form.hospital}>
            <option value="">----- Select Facility -----</option>
            {facilities.map((f) => <option key={f}>{f}</option>)}
          </Select>
        </Field>
        <Field label="Date Received" required error={errors.dateReceived}>
          <TextInput type="date" value={form.dateReceived} error={errors.dateReceived} onChange={(e) => update({ dateReceived: e.target.value })} />
        </Field>
        <Field label="Medical record #" required error={errors.medicalRecordNumber}>
          <TextInput value={form.medicalRecordNumber} error={errors.medicalRecordNumber} onChange={(e) => update({ medicalRecordNumber: e.target.value })} />
        </Field>
        <Field label={`Source: ${form.source} (${form.source} ID)`}>
          <TextInput value={form.eharsId} onChange={(e) => update({ eharsId: e.target.value })} placeholder="ID" />
        </Field>
      </div>
    </div>
  );
}

export function StepContact({ form, update, errors = {} }: StepFormProps) {
  return (
    <div>
      <SectionHeading title="Patient Contact Information" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Address Type">
          <Select value={form.addressType} onChange={(e) => update({ addressType: e.target.value })}>
            {ADDRESS_TYPES.map((a) => <option key={a}>{a}</option>)}
          </Select>
        </Field>
        <Field label="Street Address 1" required error={errors.street1}>
          <TextInput value={form.street1} error={errors.street1} onChange={(e) => update({ street1: e.target.value })} />
        </Field>
        <Field label="Street Address 2">
          <TextInput value={form.street2} onChange={(e) => update({ street2: e.target.value })} />
        </Field>
        <Field label="City" required error={errors.city}>
          <TextInput value={form.city} error={errors.city} onChange={(e) => update({ city: e.target.value })} />
        </Field>
        <Field label="State">
          <TextInput value={form.state} onChange={(e) => update({ state: e.target.value })} />
        </Field>
        <Field label="ZIP Code" required error={errors.zip}>
          <TextInput value={form.zip} error={errors.zip} onChange={(e) => update({ zip: e.target.value })} />
        </Field>
        <Field label="County">
          <Select value={form.county} onChange={(e) => update({ county: e.target.value })}>
            <option value="">Select County</option>
            {COUNTIES.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Primary Phone">
          <TextInput value={form.primaryPhone} onChange={(e) => update({ primaryPhone: e.target.value })} placeholder="10 digit phone number (ex: 555-555-5555)" />
        </Field>
        <Field label="Alternate Phone">
          <TextInput value={form.alternatePhone} onChange={(e) => update({ alternatePhone: e.target.value })} placeholder="10 digit phone number (ex: 555-555-5555)" />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Comments">
          <Textarea value={form.contactComments} onChange={(e) => update({ contactComments: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

export function StepEmergency({ form, update }: StepFormProps) {
  return (
    <div>
      <SectionHeading title="Emergency Contact Information" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Emergency Contact Person">
          <TextInput value={form.ecName} onChange={(e) => update({ ecName: e.target.value })} />
        </Field>
        <Field label="Relationship">
          <Select value={form.ecRelationship} onChange={(e) => update({ ecRelationship: e.target.value })}>
            <option value="">--</option>
            {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="Phone">
          <TextInput value={form.ecPhone} onChange={(e) => update({ ecPhone: e.target.value })} placeholder="10 digit phone number (ex: 555-555-5555)" />
        </Field>
      </div>
    </div>
  );
}

export function StepMedical({ form, update }: StepFormProps) {
  return (
    <div>
      <SectionHeading title="Medical Information" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Medication">
          <TextInput value={form.medication} onChange={(e) => update({ medication: e.target.value })} />
        </Field>
        <Field label="In Care">
          <Select value={form.inCare} onChange={(e) => update({ inCare: e.target.value })}>
            <option value="">--</option><option>Yes</option><option>No</option><option>Unknown</option>
          </Select>
        </Field>
        <Field label="Disclosed">
          <Select value={form.disclosed} onChange={(e) => update({ disclosed: e.target.value })}>
            <option value="">--</option><option>Yes</option><option>No</option>
          </Select>
        </Field>
        <Field label="Pregnant">
          <Select value={form.pregnant} onChange={(e) => update({ pregnant: e.target.value })}>
            <option value="">--</option><option>Yes</option><option>No</option><option>N/A</option>
          </Select>
        </Field>
        {form.pregnant === "Yes" && (
          <Field label="EDD">
            <TextInput type="date" value={form.edd} onChange={(e) => update({ edd: e.target.value })} />
          </Field>
        )}
        <Field label="Date of LMP">
          <TextInput type="date" value={form.dateLMP} onChange={(e) => update({ dateLMP: e.target.value })} />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Additional Medical Notes">
          <Textarea value={form.medicalNotes} onChange={(e) => update({ medicalNotes: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

export function StepLinkage({ form, update }: StepFormProps) {
  const showOther = form.linkageStatus === "Others" || form.updatedLinkageStatus === "Others";
  return (
    <div>
      <SectionHeading title="Linkage to Care Status" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Linkage to Care Status">
          <Select value={form.linkageStatus} onChange={(e) => update({ linkageStatus: e.target.value })}>
            <option value="">--</option>
            {LINKAGE_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Updated Linkage Care Status">
          <Select value={form.updatedLinkageStatus} onChange={(e) => update({ updatedLinkageStatus: e.target.value })}>
            <option value="">--</option>
            {LINKAGE_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </Field>
        {showOther && (
          <Field label="Linkage Status – Other Details">
            <TextInput value={form.linkageOtherDetails} onChange={(e) => update({ linkageOtherDetails: e.target.value })} />
          </Field>
        )}
      </div>
      <div className="mt-5 flex flex-col gap-3">
        <Checkbox label="Notification sent" checked={form.notificationSent} onChange={(v) => update({ notificationSent: v })} />
        {form.notificationSent && (
          <div className="max-w-xs">
            <Field label="Date of Notification">
              <TextInput type="date" value={form.dateOfNotification} onChange={(e) => update({ dateOfNotification: e.target.value })} />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

export interface StepTestingProps {
  form: ClientRecord;
  update: (patch: Partial<ClientRecord>) => void;
  updateNested: {
    (section: "testFlags", patch: Partial<TestFlags>): void;
    (section: "hiv" | "hivUpdated", patch: Partial<HivDiagnosis>): void;
  };
}
export function StepTesting({ form, updateNested }: StepTestingProps) {
  const flags = form.testFlags;
  return (
    <div>
      <SectionHeading title="Testing Information" subtitle="Enter at least one test information." />
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => updateNested("testFlags", { t1PositiveTest: !flags.t1PositiveTest })}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
            flags.t1PositiveTest ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {flags.t1PositiveTest && <Check className="h-3.5 w-3.5" />} T1: Positive Test Result
        </button>
        <button
          onClick={() => updateNested("testFlags", { t2CaseReport: !flags.t2CaseReport })}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
            flags.t2CaseReport ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {flags.t2CaseReport && <Check className="h-3.5 w-3.5" />} T2: Case Report
        </button>
        <button className="flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-400 hover:border-cyan-600 hover:text-cyan-600">
          <Plus className="h-3.5 w-3.5" /> Add test type
        </button>
      </div>
      <div className="flex flex-wrap gap-6">
        <Checkbox label="Is Self Reported" checked={flags.isSelfReported} onChange={(v) => updateNested("testFlags", { isSelfReported: v })} />
        <Checkbox label="Is Positive Test" checked={flags.isPositiveTest} onChange={(v) => updateNested("testFlags", { isPositiveTest: v })} />
        <Checkbox label="Is Case Report" checked={flags.isCaseReport} onChange={(v) => updateNested("testFlags", { isCaseReport: v })} />
      </div>
    </div>
  );
}

export function StepPatientTreatment({ form, update, errors = {} }: StepFormProps) {
  return (
    <div>
      <SectionHeading title="Patient Treatment Info" subtitle="Treatment Information" />

      <div className="mb-5">
        <Field label="Treatment">
          <TextInput value={form.treatmentText} onChange={(e) => update({ treatmentText: e.target.value })} />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="NAAT" required error={errors.naatResult}>
          <Select value={form.naatResult} error={errors.naatResult} onChange={(e) => update({ naatResult: e.target.value })}>
            <option value="">--</option>
            {REACTIVITY_RESULTS.map((r) => <option key={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="NAAT Date">
          <TextInput type="date" value={form.naatDate} onChange={(e) => update({ naatDate: e.target.value })} />
        </Field>
        <Field label="Disclosed" required error={errors.disclosed}>
          <Select value={form.disclosed} error={errors.disclosed} onChange={(e) => update({ disclosed: e.target.value })}>
            <option value="">--</option><option>Yes</option><option>No</option>
          </Select>
        </Field>
        <Field label="Disclosed Date">
          <TextInput type="date" value={form.disclosedDate} onChange={(e) => update({ disclosedDate: e.target.value })} />
        </Field>

        <Field label="HIV Ag/Ab" required error={errors.hivAgAbResult}>
          <Select value={form.hivAgAbResult} error={errors.hivAgAbResult} onChange={(e) => update({ hivAgAbResult: e.target.value })}>
            <option value="">--</option>
            {REACTIVITY_RESULTS.map((r) => <option key={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="HIV-1/2 Ag/Ab Date">
          <TextInput type="date" value={form.hivAgAbDate} onChange={(e) => update({ hivAgAbDate: e.target.value })} />
        </Field>
        <Field label="In Care" required error={errors.inCare}>
          <Select value={form.inCare} error={errors.inCare} onChange={(e) => update({ inCare: e.target.value })}>
            <option value="">--</option><option>Yes</option><option>No</option><option>Unknown</option>
          </Select>
        </Field>
        <Field label="In Care Date">
          <TextInput type="date" value={form.inCareDate} onChange={(e) => update({ inCareDate: e.target.value })} />
        </Field>

        <Field label="HIV IA (Supplemental)" required error={errors.hivIaResult}>
          <Select value={form.hivIaResult} error={errors.hivIaResult} onChange={(e) => update({ hivIaResult: e.target.value })}>
            <option value="">--</option>
            {REACTIVITY_RESULTS.map((r) => <option key={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="HIV - 1/2 IA (Supplemental) Date">
          <TextInput type="date" value={form.hivIaDate} onChange={(e) => update({ hivIaDate: e.target.value })} />
        </Field>
        <Field label="Linkage Care Status">
          <Select value={form.linkageStatus} onChange={(e) => update({ linkageStatus: e.target.value })}>
            <option value="">--</option>
            {LINKAGE_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Medication">
          <TextInput value={form.medication} onChange={(e) => update({ medication: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

export interface StepHivProps {
  title: string;
  data: HivDiagnosis;
  onChange: (patch: Partial<HivDiagnosis>) => void;
}
export function StepHiv({ title, data, onChange }: StepHivProps) {
  return (
    <div>
      <SectionHeading title={title} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="HIV Diagnosis Date">
          <TextInput type="date" value={data.diagnosisDate} onChange={(e) => onChange({ diagnosisDate: e.target.value })} />
        </Field>
        <Field label="AIDS Date">
          <TextInput type="date" value={data.aidsDate} onChange={(e) => onChange({ aidsDate: e.target.value })} />
        </Field>
        <Field label="Mode of Transmission">
          <Select value={data.modeOfTransmission} onChange={(e) => onChange({ modeOfTransmission: e.target.value })}>
            <option value="">--</option>
            {MODES_OF_TRANSMISSION.map((m) => <option key={m}>{m}</option>)}
          </Select>
        </Field>
        <Field label="Date Reported HIV Positive to State">
          <TextInput type="date" value={data.reportedDate} onChange={(e) => onChange({ reportedDate: e.target.value })} />
        </Field>
        <Field label="Date of Last Viral Load">
          <TextInput type="date" value={data.lastViralLoadDate} onChange={(e) => onChange({ lastViralLoadDate: e.target.value })} />
        </Field>
        <Field label="Viral Load">
          <TextInput value={data.viralLoad} onChange={(e) => onChange({ viralLoad: e.target.value })} placeholder="<20" />
        </Field>
        <Field label="Date of Last CD4 Count">
          <TextInput type="date" value={data.lastCd4Date} onChange={(e) => onChange({ lastCd4Date: e.target.value })} />
        </Field>
        <Field label="CD4 Count">
          <TextInput value={data.cd4Count} onChange={(e) => onChange({ cd4Count: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

export function StepStdHistory({ form, update }: StepFormProps) {
  return (
    <div>
      <SectionHeading title="STD Treatment History" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Treatment Given">
          <Select value={form.treatmentGiven} onChange={(e) => update({ treatmentGiven: e.target.value })}>
            {TREATMENTS.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Treatment Date">
          <TextInput type="date" value={form.treatmentDate} onChange={(e) => update({ treatmentDate: e.target.value })} />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Treatment Notes">
          <Textarea value={form.treatmentNotes} onChange={(e) => update({ treatmentNotes: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

export interface AnalyteDef {
  key: AnalyteKey;
  label: string;
}
export const ANALYTES: AnalyteDef[] = [
  { key: "rpr", label: "RPR Test Information" },
  { key: "vdrl", label: "CSF-VDRL Test Information" },
  { key: "tppa", label: "TP-PA Information" },
  { key: "ftaAbs", label: "FTA-ABS Information" },
  { key: "eia", label: "EIA Information" },
  { key: "chlamydia", label: "Chlamydia [200] Test Information" },
  { key: "gonorrhea", label: "Gonorrhea [300] Test Information" },
];

export interface StepStdTestsProps {
  title: string;
  section: StdSection;
  data: SpecimenRows;
  onChange: (section: StdSection, analyte: AnalyteKey, rows: SpecimenRow[]) => void;
}
export function StepStdTests({ title, section, data, onChange }: StepStdTestsProps) {
  return (
    <div>
      <SectionHeading title={title} />
      <div className="space-y-6">
        {ANALYTES.map((a) => (
          <SpecimenTable
            key={a.key}
            label={a.label}
            rows={data[a.key]}
            onChange={(rows) => onChange(section, a.key, rows)}
          />
        ))}
      </div>
    </div>
  );
}

export interface SpecimenTableProps {
  label: string;
  rows: SpecimenRow[];
  onChange: (rows: SpecimenRow[]) => void;
}
export function SpecimenTable({ label, rows, onChange }: SpecimenTableProps) {
  function setCell(i: number, field: keyof SpecimenRow, value: string) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r));
    onChange(next);
  }
  function addRow() {
    onChange([...rows, { date: "", specimenId: "", details: "", titer: "" }]);
  }
  function removeRow(i: number) {
    onChange(rows.filter((_, idx) => idx !== i));
  }

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-4 py-2">
        <p className="text-sm font-semibold text-slate-700">{label}</p>
        <button onClick={addRow} className="flex items-center gap-1 text-xs font-medium text-cyan-600 hover:text-cyan-800">
          <Plus className="h-3.5 w-3.5" /> Add row
        </button>
      </div>
      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="text-xs font-medium uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2">Specimen Collection Date</th>
                <th className="px-4 py-2">Specimen ID</th>
                <th className="px-4 py-2">Details</th>
                <th className="px-4 py-2">Titer</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-50">
                  <td className="px-4 py-1.5"><input type="date" value={r.date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCell(i, "date", e.target.value)} className={inputCls} /></td>
                  <td className="px-4 py-1.5"><input value={r.specimenId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCell(i, "specimenId", e.target.value)} className={inputCls} /></td>
                  <td className="px-4 py-1.5"><input value={r.details} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCell(i, "details", e.target.value)} className={inputCls} /></td>
                  <td className="px-4 py-1.5"><input value={r.titer} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCell(i, "titer", e.target.value)} className={inputCls} /></td>
                  <td className="px-2 py-1.5"><button onClick={() => removeRow(i)} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-4 py-4 text-xs text-slate-400">No results recorded.</p>
      )}
    </div>
  );
}

export function StepNotes({ form, update }: StepFormProps) {
  return (
    <div>
      <SectionHeading title="Additional Notes" />
      <Field label="Treatment/Additional Notes">
        <Textarea maxLength={1500} value={form.additionalNotes} onChange={(e) => update({ additionalNotes: e.target.value })} />
      </Field>
      <p className="mb-4 mt-1 text-right text-xs text-slate-400">Character Count: {form.additionalNotes.length} / 1500</p>

      <Field label="DOH Notes/Outcome">
        <Textarea maxLength={1500} value={form.dohNotes} onChange={(e) => update({ dohNotes: e.target.value })} />
      </Field>
      <p className="mt-1 text-right text-xs text-slate-400">Character Count: {form.dohNotes.length} / 1500</p>
    </div>
  );
}

export interface StepSummaryProps {
  form: ClientRecord;
  goTo: (idx: number) => void;
}
export function StepSummary({ form, goTo }: StepSummaryProps) {
  function stdCounts(rows: SpecimenRows): string {
    const parts = ANALYTES.map((a) => `${a.label.replace(" Test Information", "").replace(" Information", "")}: ${rows[a.key].length}`);
    return parts.join(" · ");
  }

  const groups: { title: string; stepIdx: number; rows: [string, string][] }[] = [
    {
      title: "Patient Identification", stepIdx: 0,
      rows: [
        ["Name", `${form.firstName} ${form.middleName} ${form.lastName}`.replace(/\s+/g, " ").trim() || "—"],
        ["Date of birth", form.dob || "—"],
        ["Birth sex", form.birthSex || "—"],
        ["Source", form.source || "—"],
        ["Hospital / Facility", [form.hospital, form.facility].filter(Boolean).join(" – ") || "—"],
        ["Date received", form.dateReceived || "—"],
        ["Medical record #", form.medicalRecordNumber || "—"],
      ],
    },
    {
      title: "Patient Contact Information", stepIdx: 1,
      rows: [
        ["Address", [form.street1, form.street2, form.city, form.state, form.zip].filter(Boolean).join(", ") || "—"],
        ["County", form.county || "—"],
        ["Primary phone", form.primaryPhone || "—"],
        ["Alternate phone", form.alternatePhone || "—"],
      ],
    },
    {
      title: "Emergency Contact Information", stepIdx: 2,
      rows: [
        ["Contact person", form.ecName || "—"],
        ["Relationship", form.ecRelationship || "—"],
        ["Phone", form.ecPhone || "—"],
      ],
    },
    {
      title: "Medical Information", stepIdx: 3,
      rows: [
        ["Medication", form.medication || "—"],
        ["In care", form.inCare || "—"],
        ["Disclosed", form.disclosed || "—"],
        ["Pregnant", form.pregnant || "—"],
      ],
    },
    {
      title: "Linkage to Care Status", stepIdx: 4,
      rows: [
        ["Linkage to care status", form.linkageStatus || "—"],
        ["Updated linkage care status", form.updatedLinkageStatus || "—"],
        ["Notification sent", form.notificationSent ? `Yes (${form.dateOfNotification || "no date"})` : "No"],
      ],
    },
    {
      title: "Testing Information", stepIdx: 5,
      rows: [
        ["T1: Positive test result", form.testFlags.t1PositiveTest ? "Yes" : "No"],
        ["T2: Case report", form.testFlags.t2CaseReport ? "Yes" : "No"],
        ["Self reported / Positive test / Case report", `${form.testFlags.isSelfReported ? "Self-reported" : ""} ${form.testFlags.isPositiveTest ? "Positive" : ""} ${form.testFlags.isCaseReport ? "Case report" : ""}`.trim() || "—"],
      ],
    },
    {
      title: "Patient Treatment Info", stepIdx: 6,
      rows: [
        ["Treatment", form.treatmentText || "—"],
        ["NAAT", `${form.naatResult || "—"}${form.naatDate ? ` (${form.naatDate})` : ""}`],
        ["Disclosed", `${form.disclosed || "—"}${form.disclosedDate ? ` (${form.disclosedDate})` : ""}`],
        ["HIV Ag/Ab", `${form.hivAgAbResult || "—"}${form.hivAgAbDate ? ` (${form.hivAgAbDate})` : ""}`],
        ["In Care", `${form.inCare || "—"}${form.inCareDate ? ` (${form.inCareDate})` : ""}`],
        ["HIV IA (Supplemental)", `${form.hivIaResult || "—"}${form.hivIaDate ? ` (${form.hivIaDate})` : ""}`],
        ["Linkage Care Status", form.linkageStatus || "—"],
        ["Medication", form.medication || "—"],
      ],
    },
    {
      title: "HIV Diagnosis Details", stepIdx: 7,
      rows: [
        ["Diagnosis date", form.hiv.diagnosisDate || "—"],
        ["Mode of transmission", form.hiv.modeOfTransmission || "—"],
        ["Viral load", form.hiv.viralLoad || "—"],
        ["CD4 count", form.hiv.cd4Count || "—"],
      ],
    },
    {
      title: "Updated HIV Diagnosis Details", stepIdx: 8,
      rows: [
        ["Diagnosis date", form.hivUpdated.diagnosisDate || "—"],
        ["Mode of transmission", form.hivUpdated.modeOfTransmission || "—"],
        ["Viral load", form.hivUpdated.viralLoad || "—"],
        ["CD4 count", form.hivUpdated.cd4Count || "—"],
      ],
    },
    {
      title: "STD Treatment History", stepIdx: 9,
      rows: [
        ["Treatment given", form.treatmentGiven || "—"],
        ["Treatment date", form.treatmentDate || "—"],
      ],
    },
    {
      title: "STD Test Information", stepIdx: 10,
      rows: [["Results recorded", stdCounts(form.stdTests)]],
    },
    {
      title: "Updated STD Test Information", stepIdx: 11,
      rows: [["Results recorded", stdCounts(form.stdTestsUpdated)]],
    },
    {
      title: "Additional Notes", stepIdx: 12,
      rows: [
        ["Treatment/additional notes", form.additionalNotes || "—"],
        ["DOH notes/outcome", form.dohNotes || "—"],
      ],
    },
  ];

  return (
    <div>
      <SectionHeading title="Summary" subtitle="Review everything below, then save to submit this record." />
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.title} className="rounded-lg border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
              <p className="text-sm font-semibold text-slate-700">{g.title}</p>
              <button onClick={() => goTo(g.stepIdx)} className="flex items-center gap-1 text-xs font-medium text-cyan-600 hover:text-cyan-800">
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 px-4 py-3 sm:grid-cols-2">
              {g.rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 text-sm sm:justify-start">
                  <dt className="w-48 shrink-0 text-slate-400">{k}</dt>
                  <dd className="min-w-0 break-words text-slate-700">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

